import * as React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { AssetData, AssemblyDetails, SiteMetadata } from "../../lib/Types";
import { useQuery } from "@tanstack/react-query";
import {
  getBessCyclesAnalyticsQuery,
  getSiteMetaDataAssembliesQuery,
  getManufacturersListQuery,
  getBessEmissionsAnalyticsQuery,
  getGensetRunHoursAnalyticsQuery,
} from "../../lib/Queries";
import Loading from "../Shared/Loading";
import { Typography, useTheme } from "@mui/material";
import VerticalProgressBar from "../Shared/VerticalProgressBar";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import SgipGraph from "../Graphs/Common/SgipGraph";
import SgipGraphLegend from "../Graphs/Common/SgipGraphLegend";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { formatNumberOrNull } from "../Graphs/Logic/formatUtils";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import SgipCsvDownload from "./SgipCsvDownload";
import useUserStore from "./../../state/UserStore";
import { useNavigate } from "react-router-dom";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { ReactComponent as BessLogo } from "../../static/images/enapter-energy-storage-100.svg";
import { ReactComponent as SolarLogo } from "../../static/images/enapter-solar-panel-100.svg";
import { ReactComponent as GensetLogo } from "../../static/images/enapter-diesel-generator-100.svg";

import { getRunHours } from "../../lib/analyticsUtil";
import { getAlarmFormattedDateString, getAlarmFormattedTimeString } from "../../lib/Util";
import { getStatusAlarm } from "../../lib/AlarmUtil";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </Box>
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  assetData: AssetData;
  siteData: SiteMetadata;
  setNextAsset: (forwardDirection?: boolean) => void;
}

export default function AssetDetailModal({
  isOpen,
  onClose,
  assetData,
  siteData,
  setNextAsset,
}: Props) {
  const theme = useTheme();
  const [tabValue, setTabValue] = React.useState(0);
  const [sgipYearIndex, setSgipYearIndex] = React.useState(0);

  const isBess = assetData?.scaleos_type === "BESS";
  const isSolar = assetData?.scaleos_type === "Solar";
  const isGenset = assetData?.scaleos_type === "Genset";
  const navigate = useNavigate();
  const userStore = useUserStore((state) => state);

  const hasSgip: boolean = Boolean(siteData.sgip_start_date && siteData.sgip_capacity);

  const statusAlarm = getStatusAlarm(assetData?.alarms);
  const alarmColorOverride = statusAlarm || assetData?.power === null ? "textSecondary" : "primary";

  const { data: bessCyclesData } = useQuery({
    ...getBessCyclesAnalyticsQuery(siteData.uuid),
    enabled: isOpen && hasSgip,
  });

  const { data: assemblyMetadata } = useQuery({
    ...getSiteMetaDataAssembliesQuery(siteData.uuid),
    enabled: isOpen,
  });

  const { data: manufacturersList } = useQuery({
    ...getManufacturersListQuery(assetData?.scaleos_type),
    enabled: isOpen,
  });

  const { data: bessEmissionsData } = useQuery({
    ...getBessEmissionsAnalyticsQuery(siteData.uuid),
    enabled: isOpen && hasSgip,
  });

  const { data: gensetRunHoursData } = useQuery({
    ...getGensetRunHoursAnalyticsQuery(siteData.uuid, assetData?.uuid),
    enabled: isOpen && isGenset,
  });

  const getSgipYearDisplay = (index: number) => {
    return `${bessCyclesData?.[index].first_year}-${bessCyclesData?.[index].last_year}`;
  };

  const handleClick = () => {
    const orgNameFormatted = siteData.customer_name.replace(/ /g, "-");
    const siteNameFormatted = siteData.site_name.replace(/ /g, "-");
    navigate(`/organization/${orgNameFormatted}/sites/${siteNameFormatted}`);
  };

  const getVerticalProgressBar = (percentage: number, width: number) => {
    const getColor = () => {
      if (statusAlarm || assetData?.power === null) {
        return theme.palette.grey.dark;
      }
      if (percentage < 0) {
        return "rgba(0, 122, 153, 0.3)";
      }
      return theme.palette.primary.main;
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <VerticalProgressBar percentage={Math.abs(percentage)} widthPx={width} color={getColor()} />
        <Typography variant="overline" color={getColor()}>
          {Number.isFinite(percentage) ? `${Math.round(percentage)}%` : "--"}
        </Typography>
      </Box>
    );
  };

  const styles = {
    modalBox: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "50%",
      maxWidth: "800px",
      maxHeight: "90%",
      bgcolor: "background.paper",
      boxShadow: 24,
      borderRadius: "5px",
      padding: "2rem",
      minHeight: "150px",
      overflow: "auto",
    },
    contentContainer: {
      display: "flex",
      marginTop: "1rem",
      justifyContent: "space-between",
    },
    leftColumn: {
      display: "flex",
      flexDirection: "column",
    },
    rightColumn: {
      display: "flex",
      alignItems: "flex-start",
    },
    sgipContainer: {
      display: "flex",
      marginTop: "2rem",
      flexDirection: "column",
    },
    tooltip: {
      fontSize: "10px",
    },
    navigationContainer: {
      display: "flex",
      justifyContent: "left",
      gap: ".5rem",
      marginTop: "2rem",
    },
    navigationButton: {
      px: "10px",
      minWidth: "0",
    },
  };

  const getAssetDetailsSection = () => {
    const assetMetadata: AssemblyDetails | undefined = assemblyMetadata?.find(
      (assembly) => assembly.uuid === assetData.uuid,
    );

    const manufacturer = manufacturersList?.find((mfr) => mfr.uuid === assetMetadata?.manufacturer);
    const assetModel = manufacturer?.model?.find((model) => model.uuid === assetMetadata?.model);

    const powerPercentage = assetData.percentage;
    const energyPercentage = assetData.soc!;
    const power = formatNumberOrNull(assetData.power);
    const powerCapacity = formatNumberOrNull(assetData.power_capacity);
    const energy = formatNumberOrNull(assetData.current_energy_storage);
    const energyCapacity = formatNumberOrNull(assetData.energy_capacity);
    const source = assetMetadata?.source_info[0].source;

    return (
      <Box>
        <Box sx={styles.contentContainer}>
          <Box sx={styles.leftColumn}>
            <Box>
              <Typography variant="h5" color="inherit" sx={{ fontWeight: 600 }}>
                {assetMetadata?.scaleos_name || assetMetadata?.name}
              </Typography>
              <Typography variant="subtitle1" color="inherit" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                {manufacturer?.name || "—"} {assetModel?.name || "—"}
              </Typography>
              <Typography variant="caption" color="inherit" sx={{ color: "rgba(0, 0, 0, 0.38)" }}>
                {assetData.scaleos_name || assetData.name} • {source}
              </Typography>
              {statusAlarm && (
                <Stack>
                  <Typography variant="caption" color="error">
                    <Box display="flex" justifyContent="center" alignItems="center" gap="4px">
                      {statusAlarm.type === "diagnostic" ? <WifiOffIcon /> : <HelpOutlineIcon />}
                      <Box>{statusAlarm.label}</Box>
                      {`${
                        statusAlarm.alarm
                          ? `(${getAlarmFormattedDateString(
                              statusAlarm.alarm.date,
                              siteData.timezone || undefined,
                            )} ${getAlarmFormattedTimeString(
                              statusAlarm.alarm.date,
                              siteData.timezone || null,
                            )})`
                          : ""
                      }`}
                    </Box>
                  </Typography>
                </Stack>
              )}
              {isGenset && (
                <Box sx={{ display: "flex", gap: "0.3rem", marginBottom: "0" }}>
                  <Typography variant="caption" color={alarmColorOverride}>
                    {gensetRunHoursData
                      ? formatNumberOrNull(
                          // @ts-ignore
                          getRunHours(assetData.name, gensetRunHoursData),
                        )
                      : "—"}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="inherit"
                    sx={{ color: "rgba(0, 0, 0, 0.6)" }}
                  >
                    lifetime run hours
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: "0.5rem", marginBottom: "0", marginTop: "1rem" }}>
              <Typography variant="body1" color={alarmColorOverride}>
                {power}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                / {powerCapacity} kW
              </Typography>
            </Box>

            {isBess && (
              <Box sx={{ display: "flex", gap: "0.5rem", marginBottom: "0" }}>
                <Typography variant="body2" color={alarmColorOverride}>
                  {energy}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  / {energyCapacity} kWh
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={styles.rightColumn}>
            <Box sx={{ height: "100px", display: "flex", gap: "4px", alignItems: "top" }}>
              {getVerticalProgressBar(powerPercentage, 16)}
              {isBess && getVerticalProgressBar(energyPercentage, 8)}
              {isBess && <BessLogo style={{ width: "80px", top: "-10px", position: "relative" }} />}
              {isSolar && (
                <SolarLogo style={{ width: "80px", top: "-10px", position: "relative" }} />
              )}
              {isGenset && (
                <GensetLogo
                  style={{ width: "80px", top: "-10px", marginLeft: "10px", position: "relative" }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const cyclesTooltipContents = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <Typography sx={styles.tooltip}>
        Displays BESS cycles performance against SGIP targets. Includes total of all batteries at
        this site.
      </Typography>
      <Typography sx={styles.tooltip}>
        &ldquo;Data completeness&rdquo; discloses any missing data..
      </Typography>
      <Typography sx={styles.tooltip}>
        Logic: Total Discharge Since Start Date / Approved Capacity = Battery Cycles
      </Typography>
    </Box>
  );

  const emissionsTooltipContents = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <Typography sx={styles.tooltip}>
        Displays emissions impact of BESS throughput against SGIP targets. Includes total of all
        batteries at this site.
      </Typography>
      <Typography sx={styles.tooltip}>
        &ldquo;Data completeness&rdquo; discloses any missing data.
      </Typography>
      <Typography sx={styles.tooltip}>
        Logic: Emissions Impact (kg CO2e) = (Σ(Battery kWh * MOER Since Start Date)/Approved
        Capacity
      </Typography>
    </Box>
  );

  const getLastNonNullValue = (data: any[]) => {
    return data.filter((entry) => entry.value !== null).slice(-1)[0]?.value;
  };

  const getLastNonNullPreviousYearValue = (data: any[]) => {
    return data.filter((entry) => entry.previousYearValue !== null).slice(-1)[0]?.previousYearValue;
  };

  // Extracted timeseries data mapping for reuse
  const processCyclesTimeseriesData = (bessCyclesData: any, sgipYearIndex: number) => {
    return (
      bessCyclesData?.[sgipYearIndex]?.time_series.map((entry: any) => ({
        day: entry.day,
        value: entry.cummulative_bess_cycles,
        previousYearValue: entry.cummulative_bess_cycles_previous_year ?? null,
      })) ?? []
    );
  };

  const processEmissionsTimeseriesData = (bessEmissionsData: any, sgipYearIndex: number) => {
    return (
      bessEmissionsData?.[sgipYearIndex]?.time_series.map((entry: any) => ({
        day: entry.day,
        value: entry.cumulative_emissions ? entry.cumulative_emissions * -1 : null,
        previousYearValue: entry.cumulative_emissions_previous_year
          ? entry.cumulative_emissions_previous_year * -1
          : null,
      })) ?? []
    );
  };

  const getCyclesGraph = () => {
    // map data to standard SgipGraph timeseries type
    const timeseriesData = processCyclesTimeseriesData(bessCyclesData, sgipYearIndex);

    const lastChargeValue = getLastNonNullValue(timeseriesData);
    const lastPreviousYearValue = getLastNonNullPreviousYearValue(timeseriesData);

    const lastDate = timeseriesData[timeseriesData.length - 1].day;
    const lastDateFormatted = new Date(lastDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    return (
      <Box sx={{ display: "flex", marginTop: "1rem" }}>
        <SgipGraph
          timeseriesData={timeseriesData}
          targetValueEnd={104}
          topSuccessRegion={true}
          flipValues={false}
          primaryLabel="Cycles to date"
          previousYearLabel="Prior year"
        />
        <Box sx={{ width: "320px", marginLeft: 0 }}>
          <SgipGraphLegend
            primaryLabel="SGIP Cycles"
            primaryValue={lastChargeValue ?? "—"}
            targetLabel={`Target min. by ${lastDateFormatted}`}
            targetValue={104}
            title="SGIP Cycles"
            previousYearLabel="Prior year"
            previousYearValue={lastPreviousYearValue ?? "—"}
          />
        </Box>
      </Box>
    );
  };

  const getEmissionsGraph = () => {
    // map data to standard SgipGraph timeseries type
    const timeseriesData = processEmissionsTimeseriesData(bessEmissionsData, sgipYearIndex);

    const lastEmissionsValue = getLastNonNullValue(timeseriesData);
    const lastPreviousYearValue = getLastNonNullPreviousYearValue(timeseriesData);
    const targetEnd = -5;
    const lastDate = timeseriesData[timeseriesData.length - 1].day;
    const lastDateFormatted = new Date(lastDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    return (
      <Box sx={{ display: "flex", marginTop: "1rem" }}>
        <SgipGraph
          timeseriesData={timeseriesData}
          targetValueEnd={targetEnd}
          flipValues={true}
          topSuccessRegion={false}
          primaryLabel="Emissions to date"
          previousYearLabel="Prior year"
        />
        <Box sx={{ width: "320px", marginLeft: 0 }}>
          <SgipGraphLegend
            primaryLabel="Progress to date"
            primaryValue={formatNumberOrNull(lastEmissionsValue)}
            targetLabel={`Target min. by ${lastDateFormatted}`}
            targetValue={formatNumberOrNull(targetEnd)}
            title="Emissions"
            previousYearLabel="Prior year"
            previousYearValue={lastPreviousYearValue ?? "—"}
          />
        </Box>
      </Box>
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleClose = () => {
    setTabValue(0);
    setSgipYearIndex(0);
    onClose();
  };

  const getAccuracy = (accuracy: number | undefined) => {
    return (
      <Typography variant="caption" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
        {formatNumberOrNull(accuracy)}% data completeness
      </Typography>
    );
  };

  const getTitleTooltip = (title: string, tooltipContents: string | React.ReactElement) => {
    return (
      <Box>
        <Typography variant="subtitle1" component="span">
          {title}
        </Typography>
        <Tooltip title={tooltipContents}>
          <InfoOutlinedIcon
            fontSize="small"
            color="inherit"
            sx={{ color: "rgba(0, 0, 0, 0.56)", marginLeft: "0.4rem" }}
          />
        </Tooltip>
      </Box>
    );
  };

  const getYearSelector = () => {
    if (!bessCyclesData) return null;

    return (
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel id="sgip-year-select-label">SGIP Year</InputLabel>
        <Select
          labelId="sgip-year-select-label"
          value={sgipYearIndex}
          onChange={(e) => setSgipYearIndex(Number(e.target.value))}
          label="SGIP Year"
        >
          {bessCyclesData.map((_, index) => (
            <MenuItem key={index} value={index}>
              {getSgipYearDisplay(index)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  // CSV column names
  const cyclesHeaders = [
    { label: "Date", key: "day" },
    { label: "SGIP Cycles", key: "value" },
    { label: "Previous Year Cycles", key: "previousYearValue" },
  ];
  const emissionsHeaders = [
    { label: "Date", key: "day" },
    { label: "Emission Progress to Date", key: "value" },
    { label: "Previous Year Emission Progress to Date", key: "previousYearValue" },
  ];

  const getCyclesSection = () => {
    // Prepare timeseries data and filename for CSV download
    const timeseriesData = processCyclesTimeseriesData(bessCyclesData, sgipYearIndex);
    const filename = `SGIP_Cycles_${getSgipYearDisplay(sgipYearIndex)}.csv`;
    return (
      <>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {getTitleTooltip("SGIP Cycles", cyclesTooltipContents)}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {getYearSelector()}
            <SgipCsvDownload
              timeseriesData={timeseriesData}
              filename={filename}
              headers={cyclesHeaders}
            />
          </Box>
        </Box>
        {getAccuracy(bessCyclesData?.[sgipYearIndex].accuracy)}
        {getCyclesGraph()}
      </>
    );
  };

  const getEmissionsSection = () => {
    // Prepare timeseries data and filename for CSV download
    const timeseriesData = processEmissionsTimeseriesData(bessEmissionsData, sgipYearIndex);
    const filename = `SGIP_Emissions_${getSgipYearDisplay(sgipYearIndex)}.csv`;
    return (
      <>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {getTitleTooltip("SGIP Emissions", emissionsTooltipContents)}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {getYearSelector()}
            <SgipCsvDownload
              timeseriesData={timeseriesData}
              filename={filename}
              headers={emissionsHeaders}
            />
          </Box>
        </Box>
        {getAccuracy(bessEmissionsData?.[sgipYearIndex].accuracy)}
        {getEmissionsGraph()}
      </>
    );
  };

  const getLoadingSection = (message?: string) => {
    return (
      <Box sx={{ width: "100%", paddingRight: "1rem" }}>
        <Box
          sx={{
            height: "250px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(250, 250, 250, 1)",
            border: "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          {message ? (
            <Typography variant="body1" color="inherit" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
              {message}
            </Typography>
          ) : (
            <Loading />
          )}
        </Box>
      </Box>
    );
  };

  const getSgipSection = () => {
    return (
      <Box sx={styles.sgipContainer}>
        <Box>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              "& .MuiTab-root": {
                color: "rgba(0, 0, 0, 0.6)",
                "&.Mui-selected": {
                  color: "primary.main",
                },
              },
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Tab label="SGIP Cycles" />
            <Tab label="SGIP Emissions" />
          </Tabs>
        </Box>

        {/* Extra null vs undefined check so we can show when we're missing SGIP data vs loading.  Endpoints return 204 when there is no data. */}
        <TabPanel value={tabValue} index={0}>
          {!bessCyclesData
            ? bessCyclesData === null
              ? getLoadingSection("No SGIP data available")
              : getLoadingSection()
            : getCyclesSection()}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {!bessEmissionsData
            ? bessEmissionsData === null
              ? getLoadingSection("No SGIP data available")
              : getLoadingSection()
            : getEmissionsSection()}
        </TabPanel>
      </Box>
    );
  };

  const getNavigationSection = () => {
    return (
      <Box
        sx={{
          ...styles.navigationContainer,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setNextAsset(false)}
            aria-label="Previous asset"
            sx={styles.navigationButton}
          >
            <ChevronLeftIcon fontSize="small" />
          </Button>
          <Button
            variant="outlined"
            onClick={() => setNextAsset(true)}
            aria-label="Next asset"
            sx={styles.navigationButton}
          >
            <ChevronRightIcon fontSize="small" />
          </Button>
        </Box>
        {userStore.isAdmin() && (
          <Box display="flex" alignItems="center" onClick={handleClick} sx={{ cursor: "pointer" }}>
            <SettingsOutlinedIcon fontSize="small" color="primary" sx={{ mr: 0.5 }} />
            <Typography variant="body2" color="primary">
              Asset Settings
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const isAssetDetailsLoading = !assemblyMetadata || !manufacturersList || !assetData;

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="asset-detail-modal">
      <Box sx={styles.modalBox}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="caption" color="inherit" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
            Asset Details
          </Typography>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {isAssetDetailsLoading ? <Loading /> : getAssetDetailsSection()}
        {isBess && hasSgip && getSgipSection()}
        {getNavigationSection()}
      </Box>
    </Modal>
  );
}
